import React from 'react';
import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { INFINITE_CACHE_AND_STALE_TIME } from 'constants/index';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import {
  generateFilter,
  generateSortQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import type { DropdownOption } from 'types/common/DropdownOption';
import type {
  KeyObjectiveResponse,
  KeyObjective,
} from 'types/entityTypes/KeyObjectives';

const getKeyObjectives = (categoryID: string | null = null) => {
  const queryArray = [generateSortQuery('sortOrder', 'Acs')];
  if (categoryID) {
    queryArray.push(generateFilter('categoryID', categoryID));
  }

  const url = generateURL(SPARK_RESOURCE_TYPES.KEY_OBJECTIVES, queryArray);
  return fetchWrapper<KeyObjectiveResponse>({
    url: url,
    method: 'GET',
  });
};

export function useKeyObjectives(categoryID: string | null = null) {
  const { data, ...rest } = useQuery(
    ['kyeObjectives', categoryID],
    () => getKeyObjectives(categoryID),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Kye Objectives: ${error.message}`);
        }
      },
      ...INFINITE_CACHE_AND_STALE_TIME,
    }
  );

  const { data: responseData, ...restData } = data || {};

  const idMap = responseData?.reduce<Record<string, KeyObjective>>(
    (acc, curr) => {
      return { ...acc, [curr.id]: curr };
    },
    {}
  );

  const keyObjectivesIdByNameMap = React.useMemo(() => {
    return responseData?.reduce<Record<string, string>>((acc, curr) => {
      return { ...acc, [curr.attributes.name]: curr.id };
    }, {});
  }, [responseData]);

  const keyObjectivesNameBytIdMap = React.useMemo(() => {
    return responseData?.reduce<Record<string, string>>((acc, curr) => {
      return { ...acc, [curr.id]: curr.attributes.name };
    }, {});
  }, [responseData]);

  const dropdownOptions: DropdownOption[] =
    responseData?.map((type) => ({
      label: type.attributes.name,
      value: type.id,
    })) || [];

  return {
    keyObjectives: responseData,
    keyObjectivesIdByNameMap,
    keyObjectivesNameBytIdMap,
    idMap,
    dropdownOptions,
    ...restData,
    ...rest,
  };
}
